import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { ContributionRecord } from '../types';

interface ContributionHistoryProps {
  records: ContributionRecord[];
  canEdit: boolean;
  onEdit: (record: ContributionRecord) => void;
  onVerify: (record: ContributionRecord) => void;
}

const ContributionHistory = ({ records, canEdit, onEdit, onVerify }: ContributionHistoryProps) => {
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<string>('desc');

  const formatCurrency = (amount: number) => {
    return `${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} VT`;
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  const formatDateTime = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(date);
  };

  const getStatusBadge = (status: ContributionRecord['verificationStatus']) => {
    switch (status) {
      case 'verified':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-success/10 text-success text-xs font-medium">
            <Icon name="CheckCircle" size={14} />
            Verified
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-warning/10 text-warning text-xs font-medium">
            <Icon name="Clock" size={14} />
            Pending
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-error/10 text-error text-xs font-medium">
            <Icon name="XCircle" size={14} />
            Rejected
          </span>
        );
    }
  };

  const filteredRecords = records
    .filter(record => filterStatus === 'all' || record.verificationStatus === filterStatus)
    .sort((a, b) => {
      const dateA = a.date.getTime();
      const dateB = b.date.getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'verified', label: 'Verified' },
    { value: 'pending', label: 'Pending' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const sortOptions = [
    { value: 'desc', label: 'Newest First' },
    { value: 'asc', label: 'Oldest First' }
  ];

  return (
    <div className="bg-card rounded-md border border-border shadow-elevation-1">
      <div className="p-6 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Icon name="History" size={20} />
            Contribution History ({filteredRecords.length})
          </h2>

          <div className="flex flex-wrap gap-3">
            <Select
              options={statusOptions}
              value={filterStatus}
              onChange={(value) => setFilterStatus(value as string)}
              placeholder="Filter by status"
              className="w-40"
            />
            <Select
              options={sortOptions}
              value={sortOrder}
              onChange={(value) => setSortOrder(value as string)}
              placeholder="Sort order"
              className="w-40"
            />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        {filteredRecords.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Icon name="FileText" size={32} className="text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">No contribution records found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Recording Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Recorded By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Verified By
                </th>
                {canEdit && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredRecords.map((record) => (
                <tr key={record.id} className="hover:bg-muted/50 transition-interactive">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-foreground">
                      {formatDate(record.date)}
                    </div>
                    {record.editedAt && (
                      <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        <Icon name="Edit" size={12} />
                        Edited {formatDateTime(record.editedAt)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-muted-foreground">
                      {record.recordingDate ? formatDate(record.recordingDate) : 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-foreground">
                      {formatCurrency(record.amount)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-foreground">{record.recordedBy}</div>
                    {record.editedBy && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Edited by {record.editedBy}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(record.verificationStatus)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {record.verifiedBy ? (
                      <div>
                        <div className="text-sm text-foreground">{record.verifiedBy}</div>
                        {record.verifiedAt && (
                          <div className="text-xs text-muted-foreground">
                            {formatDateTime(record.verifiedAt)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">-</span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Edit"
                          onClick={() => onEdit(record)}
                        >
                          Edit
                        </Button>
                        {record.verificationStatus === 'pending' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="CheckCircle"
                            onClick={() => onVerify(record)}
                          >
                            Verify
                          </Button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {filteredRecords.length > 0 && (
        <div className="p-4 border-t border-border bg-muted/50">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>Showing {filteredRecords.length} of {records.length} records</span>
            <div className="flex items-center gap-2">
              <Icon name="Shield" size={16} />
              <span>All data encrypted and audit logged</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContributionHistory;